import { getAssetPath } from '@/constants/configs';

import { AccountTypeStatus } from '@/types/api/account';
import { QuestionCode } from '@/types/api/pep';
import {
  AppConfigurations,
  ExtraBasicInfoQuestions,
} from '@/types/app-configurations';
import { DocumentType } from '@/types/document';
import { User } from '@/types/user';

import {
  isBusinessPurposeQuestionEnabled,
  isMinorUser,
  isRemediationCustomer,
} from '@/utils/user';

import {
  defaultConfigs,
  getExtraBasicInfoQuestions as defaultGetExtraBasicInfoQuestions,
  resolveICBWebUrl,
} from './default';

export const trinidadConfigs: AppConfigurations = {
  ...defaultConfigs,
  links: {
    ...defaultConfigs.links,
    privacyPolicyURL: 'https://www.republictt.com/about/privacy-policy',
    termsAndConditionsURL: 'https://republiconline.republictt.com/legal.html',
    termsOfUseURL: getAssetPath('/tnc/republiconboard-tnc-tto.pdf'),
    appTermsAndConditionsURL: getAssetPath('/tnc/republiconboard-tnc-tto.pdf'),
    biometricConsentURL: getAssetPath(
      '/bio-consent/republiconboard-biometric-consent-tto.pdf',
    ),
    icbWeb: resolveICBWebUrl(
      'https://republiconlinett.rfhl.com/ICBankingTT.UI/#/administrationGeneral/login',
    ),
    icbIOS: 'https://apps.apple.com/us/app/republicmobile-tt/id1572835373',
    icbAndroid:
      'https://play.google.com/store/apps/details?id=ttic.com.republicprod&pli=1',
  },
  defaultCountryCode: '1868',
  locale: 'en-TT',
  isoCode: 'TTO',
  dateFormat: 'dd/MM/yyyy',
  defaultCurrency: 'TTD',
  excludedBranchCodes: ['18'],
  idCaptureRequirement: 'idCardOrPassport',
  idDocumentTypes: [
    DocumentType.Id,
    DocumentType.Passport,
    DocumentType.IdentityDriverLicense,
  ],
  autoApproveUnemployedUsers: true,
  addressCityOptions: [
    { text: 'Port of Spain', value: 'Port of Spain' },
    { text: 'San Fernando', value: 'San Fernando' },
    { text: 'Arima', value: 'Arima' },
    { text: 'Chaguanas', value: 'Chaguanas' },
    { text: 'Point Fortin', value: 'Point Fortin' },
    { text: 'Sangre Grande', value: 'Sangre Grande' },
    { text: 'Diego Martin', value: 'Diego Martin' },
    { text: 'Tunapuna', value: 'Tunapuna' },
    { text: 'Couva', value: 'Couva' },
    { text: 'Princes Town', value: 'Princes Town' },
    { text: 'Siparia', value: 'Siparia' },
    { text: 'Mayaro', value: 'Mayaro' },
    { text: 'Rio Claro', value: 'Rio Claro' },
    { text: 'Penal', value: 'Penal' },
    { text: 'Debe', value: 'Debe' },
    { text: 'Gasparillo', value: 'Gasparillo' },
    { text: 'Marabella', value: 'Marabella' },
    { text: 'La Brea', value: 'La Brea' },
    { text: 'Scarborough', value: 'Scarborough' },
    { text: 'Roxborough', value: 'Roxborough' },
    { text: 'Charlotteville', value: 'Charlotteville' },
  ],
  shouldRequireMaritalStatus: false,
  showRiskSectorFlagQuestion: false,
  showSelfEmployedWithRiskSectorFlagQuestion: true,
  existingCustomer: {
    maxAccountsPerType: 2,
  },
};

export const getExtraBasicInfoQuestions = (
  user: User,
): ExtraBasicInfoQuestions => {
  if (isMinorUser(user)) {
    return {
      ...defaultGetExtraBasicInfoQuestions(user),
    };
  }

  if (isRemediationCustomer(user)) {
    return {
      [QuestionCode.DualCitizenship]: {
        enabled: user.citizenship2Has === undefined,
        question: `question.${QuestionCode.DualCitizenship}`,
        priority: 1,
      },
      [QuestionCode.RemediationExpectedMonthlyDeposits]: {
        enabled: !!user.accountTypes?.some(
          account =>
            account.status === AccountTypeStatus.Active &&
            !account.estimatedMonthlyDepositAmountUpdatedInRemediation,
        ),
        question: 'questionDrawer.remediationExpectedMonthlyDeposits',
        priority: 2,
      },
      [QuestionCode.RemediationFundingSources]: {
        enabled: !!user.accountTypes?.some(
          account =>
            account.status === AccountTypeStatus.Active &&
            !account.fundingSourceUpdatedInRemediation,
        ),
        question: 'questionDrawer.remediationFundingSources',
        priority: 3,
      },
    };
  }
  return {
    ...defaultGetExtraBasicInfoQuestions(user),
    [QuestionCode.AccountPurpose]: {
      enabled: isBusinessPurposeQuestionEnabled(user),
      question: 'questionDrawer.accountPurpose.question',
      priority: 0,
    },
  };
};
